import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Collapse, IconButton, Stack, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import { ProductGroups } from 'enums';
import { LinkedinSearch, LinkedinSearchResult } from '../../../../campaign-builder.types';
import ContactsPreviewTable from './contacts-preview-table';

interface SearchItemProps {
  index: number;
  isAdded: boolean;
  search: LinkedinSearchResult;
  productGroup: ProductGroups;

  handleOpenRefineLinkedinSearchDialog(): void;
}

const SearchItem: React.FC<SearchItemProps> = ({ index, isAdded, search, productGroup, handleOpenRefineLinkedinSearchDialog }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const [expanded, setExpanded] = useState(false);

  const { searchName, contacts, linkedinFreq } = search;
  const contactsAreEmpty = _.isEmpty(contacts) || _.isNil(contacts);

  const defaultPaddingLeft = 16;
  const stepWidth = 32;
  const maxWidth = 160;

  const paddingLeft = Math.min(defaultPaddingLeft + index * stepWidth, maxWidth);

  const handleToggleExpand = () => {
    if (!contactsAreEmpty) {
      setExpanded((prev) => !prev);
    }
  };

  const openLink = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    handleOpenRefineLinkedinSearchDialog();
  };

  // Helper function to handle product-specific translations
  const getLocalizedText = (key: string, interpolation?: Record<string, unknown>): string => {
    if (productGroup === ProductGroups.LEAD_BOOSTER) {
      const leadBoosterKey = `${key}_LeadBooster`;
      return t(leadBoosterKey, interpolation) || t(key, interpolation);
    }
    // Default is for Active Sourcing
    const activeSourcingKey = `${key}_ActiveSourcing`;
    return t(activeSourcingKey, interpolation) || t(key, interpolation);
  };

  return (
    <div className={classes.searchItem}>
      <Stack
        className={classes.searchItemHeader}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={handleToggleExpand}
        gap={4}
      >
        <Stack direction={'row'} gap={1}>
          <Typography className={classes.regularText} fontWeight="bold">
            {searchName ? `${t(searchName)} ${!_.isNil(index) ? `(${index})` : ''}` : '-'}
          </Typography>
          {isAdded ? (
            <>
              <Typography className={classes.regularText} fontWeight="bold">
                -
              </Typography>
              <Typography className={cx(classes.regularText, 'highlight')}>{t('added')}</Typography>
            </>
          ) : null}
        </Stack>
        <Stack direction="row" alignItems="center" gap={1}>
          <div className={classes.linkedinFreq} style={{ paddingLeft: paddingLeft }}>
            <Typography className={classes.regularText} sx={{ whiteSpace: 'nowrap' }}>
              {getLocalizedText('talentsFoundMatchingCriteria', {
                count: linkedinFreq,
              })}
            </Typography>
          </div>
          <PaiButton text={t('optimizeAndAddSearch')} border={'round'} size={'small'} noLineBreak={true} action={openLink} />
          <IconButton className={cx(classes.searchItemExpandIcon, expanded ? 'expanded' : undefined)} size="small">
            <ExpandMoreIcon />
          </IconButton>
        </Stack>
      </Stack>

      <Collapse className={classes.searchItemCollapse} in={expanded && !contactsAreEmpty} timeout={0} unmountOnExit>
        <ContactsPreviewTable contacts={contacts} />
      </Collapse>
    </div>
  );
};

interface FoundLinkedinSearchProps {
  linkedinSearch: LinkedinSearch;
  searchResults: LinkedinSearchResult[];
  productGroup: ProductGroups;

  handleOpenRefineLinkedinSearchDialog(linkedinSearch: LinkedinSearchResult, idx: number): () => void;
}

const FoundLinkedinSearch: React.FC<FoundLinkedinSearchProps> = ({
  linkedinSearch,
  searchResults,
  productGroup,
  handleOpenRefineLinkedinSearchDialog,
}) => {
  if (_.isEmpty(searchResults)) {
    return null;
  }

  return (
    <div>
      {searchResults.map((search, index) => (
        <SearchItem
          key={index}
          index={index}
          isAdded={linkedinSearch.addedIndices.includes(index)}
          search={search}
          productGroup={productGroup}
          handleOpenRefineLinkedinSearchDialog={handleOpenRefineLinkedinSearchDialog(search, index)}
        />
      ))}
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  searchItem: {
    position: 'relative',
    background: '#FFF',
    borderRadius: '12px',
    marginBottom: '16px',
    width: '100%',
  },
  searchItemHeader: {
    padding: '8px 16px',
    cursor: 'pointer',
  },
  searchItemExpandIcon: {
    padding: '4px',
    '& svg': {
      fontSize: theme.typography.pxToRem(20),
      transition: '225ms',
    },
    '&.expanded': {
      '& svg': {
        transform: 'rotate(180deg)',
      },
    },
  },
  linkedinFreq: {
    borderRadius: '16px',
    background: theme.palette.backgrounds.main,
    padding: '4.25px 16px',
    '& p': {
      fontSize: theme.typography.pxToRem(13),
    },
  },
  searchItemCollapse: {
    padding: '0 16px 16px 16px',
  },
  regularText: {
    fontSize: theme.typography.pxToRem(15),
    '&.highlight': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
}));

export default FoundLinkedinSearch;
